import React, { useState } from "react";
import { makeStyles } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import MenuIcon from "@material-ui/icons/Menu";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import FAQDialog from "./FAQDialog";

var useStyles = makeStyles(function () {
  return {
    root: {
      display: "flex",
      alignItems: "center",
      padding: "12px 24px",
      borderBottom: "1px solid #f0f0f0",
      background: "#fff",
      minHeight: "52px",
    },
    hamburger: {
      marginRight: "8px",
      padding: "6px",
      color: "#9ca3af",
    },
    nqIcon: {
      width: "30px",
      height: "30px",
      borderRadius: "8px",
      background: "linear-gradient(135deg, #1e3a5f, #2563eb)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: "10px",
      flexShrink: 0,
      boxShadow: "0 1px 3px rgba(30,58,95,0.15)",
    },
    nqIconText: {
      color: "#fff",
      fontSize: "11px",
      fontWeight: 700,
      lineHeight: 1,
    },
    titleColumn: {
      display: "flex",
      flexDirection: "column",
    },
    title: {
      fontSize: "15px",
      fontWeight: 600,
      color: "#111827",
      lineHeight: 1.2,
      fontFamily: "'DM Sans', sans-serif",
    },
    statusRow: {
      display: "flex",
      alignItems: "center",
      gap: "4px",
      marginTop: "2px",
    },
    statusDot: {
      width: "5px",
      height: "5px",
      borderRadius: "50%",
      background: "#22c55e",
    },
    statusText: {
      fontSize: "11px",
      color: "#6b7280",
      fontFamily: "'IBM Plex Sans', sans-serif",
    },
    rightSide: {
      marginLeft: "auto",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    tipsButton: {
      padding: "6px",
      color: "#6b7280",
      border: "1px solid #e5e7eb",
      borderRadius: "7px",
      background: "transparent",
      transition: "all 150ms",
      "&:hover": {
        color: "#374151",
        background: "#f9fafb",
      },
    },
    pdfButton: {
      display: "flex",
      alignItems: "center",
      gap: "4px",
      padding: "6px 12px",
      fontSize: "12px",
      fontWeight: 500,
      color: "#6b7280",
      background: "transparent",
      border: "1px solid #e5e7eb",
      borderRadius: "7px",
      cursor: "pointer",
      fontFamily: "'DM Sans', sans-serif",
      transition: "all 150ms",
      "&:hover": {
        color: "#374151",
        background: "#f9fafb",
      },
    },
    dashboardButton: {
      display: "flex",
      alignItems: "center",
      gap: "5px",
      padding: "6px 14px",
      fontSize: "12px",
      fontWeight: 500,
      color: "#fff",
      background: "linear-gradient(135deg, #1e3a5f, #2563eb)",
      border: "none",
      borderRadius: "7px",
      cursor: "pointer",
      fontFamily: "'DM Sans', sans-serif",
      boxShadow: "0 1px 3px rgba(30,58,95,0.18)",
      transition: "all 150ms",
      "&:hover": {
        boxShadow: "0 2px 8px rgba(30,58,95,0.3)",
      },
    },
    agentBadge: {
      marginLeft: "10px",
      display: "flex",
      alignItems: "center",
      gap: "4px",
      border: "1px solid",
      borderRadius: "20px",
      padding: "3px 9px",
      fontSize: "11px",
      fontWeight: 600,
      fontFamily: "'DM Sans', sans-serif",
    },
    agentBadgeDot: {
      width: "5px",
      height: "5px",
      borderRadius: "50%",
      flexShrink: 0,
    },
  };
});

var ChatHeader = function ChatHeader({
  sidebarOpen,
  onToggleSidebar,
  activeConversationId,
  onExportPdf,
  onBackToDashboard,
  provider,
}) {
  var classes = useStyles();
  var [faqOpen, setFaqOpen] = useState(false);
  var isStrategist = provider === "agent-2";
  var agentLabel = isStrategist ? "Strategist" : "Scout";
  var agentColor = isStrategist ? "#7c3aed" : "#2563eb";
  var agentBgColor = isStrategist ? "#faf5ff" : "#eff6ff";
  var agentBorderColor = isStrategist ? "#ddd6fe" : "#bfdbfe";

  return (
    <div className={classes.root}>
      {!sidebarOpen && (
        <IconButton
          className={classes.hamburger}
          onClick={onToggleSidebar}
          size="small"
          title="Open sidebar"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#6B7280"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <line x1="9" y1="3" x2="9" y2="21" />
            <polyline points="14 9 17 12 14 15" />
          </svg>
        </IconButton>
      )}
      <div className={classes.nqIcon}>
        <span className={classes.nqIconText}>NQ</span>
      </div>
      <div className={classes.titleColumn}>
        <span className={classes.title}>NQ Assist</span>
        <div className={classes.statusRow}>
          <span className={classes.statusDot} />
          <span className={classes.statusText}>Ready</span>
        </div>
      </div>
      <div
        className={classes.agentBadge}
        style={{
          background: agentBgColor,
          borderColor: agentBorderColor,
          color: agentColor,
        }}
      >
        <span
          className={classes.agentBadgeDot}
          style={{ background: agentColor }}
        />
        {agentLabel}
      </div>
      <div className={classes.rightSide}>
        <Tooltip title="Tips: how to ask great questions">
          <IconButton
            className={classes.tipsButton}
            onClick={() => setFaqOpen(true)}
            size="small"
            aria-label="How to ask great questions"
          >
            <HelpOutlineIcon style={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        {activeConversationId && (
          <Tooltip title="Export as PDF">
            <button className={classes.pdfButton} onClick={onExportPdf}>
              <DescriptionOutlinedIcon style={{ fontSize: 14 }} />
              PDF
            </button>
          </Tooltip>
        )}
        <button className={classes.dashboardButton} onClick={onBackToDashboard}>
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            style={{ flexShrink: 0 }}
          >
            <path
              d="M7.5 1.5L3 6l4.5 4.5"
              stroke="#fff"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Finclarity
        </button>
      </div>
      <FAQDialog open={faqOpen} onClose={() => setFaqOpen(false)} />
    </div>
  );
};

export default ChatHeader;
